/**
 * [Insider+] Shared iframe shell — wrapper + loader + auth-failure modal.
 */

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import LoaderWrap from '../Loader/LoaderWrap';
import LoaderIcon from '../Loader/LoaderIcon';
import InsiderPlusAuthModal from '../Modals/InsiderPlusAuthModal/InsiderPlusAuthModal';

export const InsiderPlusPageWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
`;

const IframeContainer = styled.div`
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  overflow: hidden;
`;

const StyledIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  display: block;
  margin: 0;
  padding: 0;
`;

const IFRAME_ALLOW =
  'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; storage-access';

// Defense in depth: the iframe origin is already allow-listed, but a
// compromised child shouldn't be able to e.g. break out of origin. Granted
// capabilities (verified against insider-plus/src as of 2026-05-27):
//   allow-scripts: iframe is a React/Vite app
//   allow-same-origin: Supabase client + cookies + storage live on its origin
//   allow-forms: subscription / membership / payment form submissions
//   allow-popups: Discourse forum + webinar opens with window.open('_blank')
//   allow-popups-to-escape-sandbox: those popups need a normal browsing
//     context, not an inherited sandbox (Discourse auth + cookies)
//   allow-storage-access-by-user-activation: Storage Access API for the
//     Discourse SSO cookie hand-off
//   allow-downloads: <a download="..."> links for course resources and
//     briefing PDFs
const IFRAME_SANDBOX =
  'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-storage-access-by-user-activation allow-downloads';

const InsiderPlusIframeShell = ({
  iframeRef,
  iframeSrc,
  iframeKey,
  title,
  dataCy,
  isRecovering,
  authFailed,
  isRetrying,
  onIframeLoad,
  onRetry,
}) => (
  <InsiderPlusPageWrapper>
    <IframeContainer data-cy={dataCy}>
      <StyledIframe
        key={iframeKey}
        ref={iframeRef}
        src={iframeSrc}
        title={title}
        allow={IFRAME_ALLOW}
        sandbox={IFRAME_SANDBOX}
        allowFullScreen
        onLoad={onIframeLoad}
      />
    </IframeContainer>
    {isRecovering && (
      <LoaderWrap>
        <LoaderIcon />
      </LoaderWrap>
    )}
    <InsiderPlusAuthModal
      isOpen={authFailed}
      isRetrying={isRetrying}
      onRetry={onRetry}
    />
  </InsiderPlusPageWrapper>
);

InsiderPlusIframeShell.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  iframeRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
  iframeSrc: PropTypes.string.isRequired,
  iframeKey: PropTypes.number,
  title: PropTypes.string.isRequired,
  dataCy: PropTypes.string.isRequired,
  isRecovering: PropTypes.bool,
  authFailed: PropTypes.bool,
  isRetrying: PropTypes.bool,
  onIframeLoad: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
};

export default InsiderPlusIframeShell;
